import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';

const SignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  useEffect(() => {
    const loadReCaptcha = () => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
      script.async = true;
      document.body.appendChild(script);
    };

    if (!process.env.REACT_APP_IS_DEV) {
      loadReCaptcha();
    }

    // Check for resetEmail in localStorage
    const resetEmail = localStorage.getItem('resetEmail');
    if (resetEmail) {
      setEmail(resetEmail);
      setShowToast(true);
      setError('Password reset successful. Please sign in with your new password.');
      setIsPasswordReset(true);
      // Remove the resetEmail from localStorage
      localStorage.removeItem('resetEmail');
    }
  }, []);

  useEffect(() => {
    if (window.location.hash === '#signup') {
      setIsSignUp(true);
    }
  }, []);

  const validateInput = () => {
    if (!email || !password || (isSignUp && !name)) {
      setError("All fields are required");
      return false;
    }

    if (typeof email !== 'string' || typeof password !== 'string' || (isSignUp && typeof name !== 'string')) {
      setError("Invalid input types");
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Invalid email format");
      return false;
    }

    if (isSignUp && (
      password.length < 8 ||
      !/[A-Z]/.test(password) ||
      !/[a-z]/.test(password) ||
      !/[0-9]/.test(password) ||
      !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    )) {
      setError("Password must be at least 8 characters long and include uppercase and lowercase letters, numbers, and special characters");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setShowToast(false);

    if (!validateInput()) {
      setShowToast(true);
      return;
    }

    try {
      let token = '';
      if (!process.env.REACT_APP_IS_DEV) {
        token = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, { action: 'submit' });
      }

      const endpoint = isSignUp ? 'register' : 'login';
      const response = await fetch(`${process.env.REACT_APP_CSH_BASE_URL}/api/v1/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, name, token }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `An error occurred during ${isSignUp ? 'sign up' : 'sign in'}`);
      }

      if (isSignUp) {
        // Registration successful
        setShowSuccessModal(true);
      } else {
        // Sign in successful
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));

        // Set the user in the AuthProvider
        setUser(data.user);

        // Check if the user has the "New" rule
        if (data.user && data.user.rules && data.user.rules.includes("New")) {
          navigate('/new');
        } else {
          navigate('/');
        }
      }
    } catch (error) {
      setError(error.message);
      setShowToast(true);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
      <div className="px-8 py-6 mt-4 text-left bg-white dark:bg-gray-800 shadow-lg rounded-lg">
        <h3 className="text-2xl font-bold text-center text-gray-800 dark:text-white">
          {isSignUp ? 'Sign Up' : 'Sign In'}
        </h3>
        <form onSubmit={handleSubmit}>
          <div className="mt-4">
            {isSignUp && (
              <div>
                <label className="block text-gray-700 dark:text-gray-200" htmlFor="name">Name</label>
                <input
                  type="text"
                  placeholder="Name"
                  className="w-full px-4 py-2 mt-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600 dark:bg-gray-700 dark:text-white"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required={isSignUp}
                />
              </div>
            )}
            <div className={isSignUp ? 'mt-4' : ''}>
              <label className="block text-gray-700 dark:text-gray-200" htmlFor="email">Email</label>
              <input
                type="email"
                placeholder="Email"
                className="w-full px-4 py-2 mt-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600 dark:bg-gray-700 dark:text-white"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
              />
            </div>
            <div className="mt-4">
              <label className="block text-gray-700 dark:text-gray-200" htmlFor="password">Password</label>
              <input
                type="password"
                placeholder="Password"
                className="w-full px-4 py-2 mt-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600 dark:bg-gray-700 dark:text-white"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete={isSignUp ? "new-password" : "current-password"}
              />
            </div>
            <div className="flex items-baseline justify-center mt-4">
              <button className="px-6 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-900">
                {isSignUp ? 'Sign Up' : 'Sign In'}
              </button>
            </div>
            {!isSignUp && (
              <div className="text-center mt-2">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm text-blue-600 hover:underline"
                >
                  Forgot Password?
                </button>
              </div>
            )}
            {!isSignUp ? (
              <div className="text-center mt-4">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Don't have an account?{' '}
                </span>
                <button
                  type="button"
                  onClick={() => setIsSignUp(true)}
                  className="text-sm text-blue-600 hover:underline"
                >
                  Sign Up
                </button>
              </div>
            ) : (
              <div className="text-center mt-4">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Already have an account?{' '}
                </span>
                <button
                  type="button"
                  onClick={() => setIsSignUp(false)}
                  className="text-sm text-blue-600 hover:underline"
                >
                  Sign In
                </button>
              </div>
            )}
          </div>
        </form>
        {showToast && (
          <div id="toast-danger" className={`flex items-center w-full max-w-xs p-4 mb-4 text-gray-500 bg-white rounded-lg shadow dark:text-gray-400 dark:bg-gray-800 fixed bottom-5 right-5 ${isPasswordReset ? 'toast-success' : ''}`} role="alert">
            <div className={`inline-flex items-center justify-center flex-shrink-0 w-8 h-8 ${isPasswordReset ? 'text-green-500 bg-green-100 dark:bg-green-800 dark:text-green-200' : 'text-red-500 bg-red-100 dark:bg-red-800 dark:text-red-200'} rounded-lg`}>
              {isPasswordReset ? (
                <svg className="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 11.793a1 1 0 1 1-1.414 1.414L10 11.414l-2.293 2.293a1 1 0 0 1-1.414-1.414L8.586 10 6.293 7.707a1 1 0 0 1 1.414-1.414L10 8.586l2.293-2.293a1 1 0 0 1 1.414 1.414L11.414 10l2.293 2.293Z" />
                </svg>
              )}
              <span className="sr-only">{isPasswordReset ? 'Success' : 'Error'} icon</span>
            </div>
            <div className="ms-3 text-sm font-normal">{error}</div>
            <button type="button" className="ms-auto -mx-1.5 -my-1.5 bg-white text-gray-400 hover:text-gray-900 rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-100 inline-flex items-center justify-center h-8 w-8 dark:text-gray-500 dark:hover:text-white dark:bg-gray-800 dark:hover:bg-gray-700" onClick={() => setShowToast(false)} aria-label="Close">
              <span className="sr-only">Close</span>
              <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
              </svg>
            </button>
          </div>
        )}
        {isSignUp && showSuccessModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-md">
              <div className="flex items-center justify-center mb-4">
                <div className="inline-flex items-center justify-center flex-shrink-0 w-12 h-12 text-green-500 bg-green-100 rounded-lg dark:bg-green-800 dark:text-green-200">
                  <svg className="w-7 h-7" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z" />
                  </svg>
                </div>
              </div>
              <h3 className="mb-4 text-xl font-medium text-gray-900 dark:text-white text-center">Account Created Successfully!</h3>
              <p className="mb-6 text-sm text-gray-500 dark:text-gray-400 text-center">
                Thank you for registering. To complete your registration and get access to the system, please contact us with your registered name and email address.
              </p>
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => {
                    setEmail('');
                    setPassword('');
                    setName('');
                    setIsSignUp(false);
                    setShowSuccessModal(false);
                  }}
                  className="text-white bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-blue-500 dark:hover:bg-blue-600 dark:focus:ring-blue-800"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignIn;
